import React from 'react';
import { Grid, Typography } from '@mui/material';
import * as yup from 'yup';
import { useKeyObjectives } from 'hooks/EntityTypes/useKeyObjectives';
import { SparkForm } from 'sharedComponents/SparkForm';
import StepperBoxSkeleton from 'sharedComponents/Skeleton/StepperBoxSkeleton';
import Footer from './Footer';
import ObjectiveFormContent from './ObjectiveFormContent';
import { useGetCategories } from 'hooks/Categories';
import { CATEGORIES } from 'constants/common';

export type ObjectivesFormValues = {
  objectiveIDs: string[];
  otherObjective: string;
};

const objectivesFormValidationSchema = yup.object({
  objectiveIDs: yup.array(yup.string()).min(3).required(),
  otherObjective: yup
    .string()
    .test('requiredTest', 'Other Objective is Required', function test(value) {
      const isOtherIncluded =
        this?.options.context?.objectiveIDs?.includes('10');
      if (!isOtherIncluded) {
        return true;
      }
      if (!!value) {
        return true;
      }
      return false;
    }),
});
type ObjectivesFormProps = {
  activeStep: number;
  nextStepHandler: () => void;
  previousStepHandler: () => void;
  objectivesCollection?: ObjectivesFormValues;
  setObjectivesCollection: (values: ObjectivesFormValues) => void;
  categoryID?: string | null;
};

function ObjectivesForm({
  nextStepHandler,
  activeStep,
  previousStepHandler,
  objectivesCollection,
  setObjectivesCollection,
  categoryID,
}: ObjectivesFormProps) {
  const {
    dropdownOptions: objectivesOptions,
    keyObjectivesIdByNameMap,
    isLoading: isLoadingKeyObjectives,
  } = useKeyObjectives(categoryID);
  const { categoryIdMap, isLoading: isLoadingCategories } =
    useGetCategories(null);

  const isBusinessCategory =
    categoryIdMap?.[categoryID || '']?.attributes.name === CATEGORIES.BUSINESS;

  const isLoading = isLoadingCategories || isLoadingKeyObjectives;

  if (isLoading) {
    return <StepperBoxSkeleton />;
  }

  const objectivesFormInitialValues = {
    objectiveIDs: objectivesCollection?.objectiveIDs || [],
    otherObjective: objectivesCollection?.otherObjective || '',
  };

  const handleSubmit = (values: ObjectivesFormValues) => {
    setObjectivesCollection(values);
    nextStepHandler();
  };

  const subHeading = isBusinessCategory
    ? 'Select up to 3 key company objectives.'
    : 'Select up to 3 key objectives.';

  return (
    <SparkForm
      initialValues={objectivesFormInitialValues}
      validationSchema={objectivesFormValidationSchema}
      onSave={handleSubmit}
    >
      <Grid item padding={2} paddingLeft={0} paddingBottom={1}>
        <Typography
          fontFamily='Mont-semibold'
          justifyContent='flex-start'
          sx={{ fontSize: '1.5rem', marginTop: '1rem' }}
        >
          Objectives
        </Typography>
        <Typography
          fontFamily='Mont-regular'
          justifyContent='flex-start'
          sx={{ fontSize: '1.0rem' }}
          color='nightblack'
        >
          {subHeading}
        </Typography>
      </Grid>
      <ObjectiveFormContent
        keyObjectivesIdByNameMap={keyObjectivesIdByNameMap}
        objectivesOptions={objectivesOptions}
      />
      <Footer
        activeStep={activeStep}
        handleBack={previousStepHandler}
        isLastStep={false}
      />
    </SparkForm>
  );
}

export default ObjectivesForm;
